/**
 * CodeDiffViewer Component
 * 
 * Renders git diff output with syntax highlighting, line numbers,
 * file tree sidebar, and side-by-side or unified view options.
 */

import React, { useMemo, useState, useRef, useEffect } from 'react';
import { IonIcon } from '@ionic/react';
import { 
  swapHorizontalOutline, 
  listOutline, 
  copyOutline, 
  checkmarkOutline,
  documentOutline,
  folderOutline,
  folderOpenOutline,
  chevronForward,
  chevronDown,
  terminalOutline,
  expandOutline,
  contractOutline
} from 'ionicons/icons';
import { Diff2HtmlUI } from 'diff2html/lib/ui/js/diff2html-ui-slim.js';
import * as JsDiff from 'diff';
import 'highlight.js/styles/github-dark.css';
import 'diff2html/bundles/css/diff2html.min.css';
import './CodeDiffViewer.css';

function generateNewFileDiff(filePath, content) {
  const lines = content.split('\n');
  const lineCount = lines.length;
  const plusLines = lines.map(l => `+${l}`).join('\n');
  return `diff --git a/${filePath} b/${filePath}
new file mode 100644
index 0000000..0000000
--- /dev/null
+++ b/${filePath}
@@ -0,0 +1,${lineCount} @@
${plusLines}
`;
}

function normalizeFileContents(fileContents) {
  if (!fileContents) return {};
  if (Array.isArray(fileContents)) {
    const obj = {};
    for (const item of fileContents) {
      if (item && typeof item.path === 'string' && typeof item.content === 'string') {
        obj[item.path] = item.content;
      }
    }
    return obj;
  }
  if (typeof fileContents === 'object') return fileContents;
  return {};
}

function combineDiffWithNewFiles(diff, rawFileContents) {
  const fileContents = normalizeFileContents(rawFileContents);
  if (Object.keys(fileContents).length === 0) return diff || '';
  const existingPaths = [];
  const fileRegex = /diff --git a\/(.+?) b\/(.+?)[\r\n]/g;
  let m;
  while ((m = fileRegex.exec(diff || '')) !== null) {
    existingPaths.push(m[2]);
  }
  const isAlreadyInDiff = (fcPath) => {
    return existingPaths.some(diffPath =>
      diffPath === fcPath ||
      fcPath.endsWith('/' + diffPath) ||
      diffPath.endsWith('/' + fcPath)
    );
  };
  const newFileDiffs = Object.entries(fileContents)
    .filter(([path]) => !isAlreadyInDiff(path))
    .map(([path, content]) => generateNewFileDiff(path, content));
  if (newFileDiffs.length === 0) return diff || '';
  return ((diff || '') + '\n' + newFileDiffs.join('\n')).trim() + '\n';
}

function prettifyJsonDiff(diff) {
  if (!diff) return diff;
  const sections = diff.split(/^(?=diff --git )/m);
  const processed = sections.map(section => {
    if (!section.trim()) return section;
    const fileMatch = section.match(/\+\+\+ b\/(.+?)(?:\n|$)/);
    if (!fileMatch || !fileMatch[1].endsWith('.json')) return section;

    const hunkMatch = section.match(/@@ -\d+(?:,\d+)? \+\d+(?:,\d+)? @@[^\n]*/);
    if (!hunkMatch) return section;

    const hunkStart = section.indexOf(hunkMatch[0]) + hunkMatch[0].length + 1;
    const body = section.substring(hunkStart);
    const lines = body.split('\n');

    const oldLines = [];
    const newLines = [];
    for (const line of lines) {
      if (line.startsWith('-')) oldLines.push(line.substring(1));
      else if (line.startsWith('+')) newLines.push(line.substring(1));
      else if (line.startsWith(' ')) { oldLines.push(line.substring(1)); newLines.push(line.substring(1)); }
    }

    const oldText = oldLines.join('\n');
    const newText = newLines.join('\n');
    if (oldText.length < 500 && newText.length < 500) return section;

    let oldPretty, newPretty;
    try { oldPretty = JSON.stringify(JSON.parse(oldText), null, 2); } catch { return section; }
    try { newPretty = JSON.stringify(JSON.parse(newText), null, 2); } catch { return section; }

    const filePath = fileMatch[1];
    const patch = JsDiff.createTwoFilesPatch(`a/${filePath}`, `b/${filePath}`, oldPretty, newPretty, '', '', { context: 3 });
    const patchLines = patch.split('\n');
    const firstHunkIdx = patchLines.findIndex(l => l.startsWith('@@'));
    if (firstHunkIdx === -1) return section;

    const header = section.substring(0, section.indexOf(hunkMatch[0]));
    return `${header}${patchLines.slice(firstHunkIdx).join('\n')}\n`;
  });
  return processed.join('');
}

// Parse diff to extract file information
function parseFilesFromDiff(diff) {
  if (!diff) return [];
  
  const files = [];
  const fileRegex = /diff --git a\/(.+?) b\/(.+?)[\r\n]/g;
  
  let match;
  while ((match = fileRegex.exec(diff)) !== null) {
    const filePath = match[2];
    const fileName = filePath.split('/').pop();
    const directory = filePath.substring(0, filePath.lastIndexOf('/')) || '';
    
    // Count additions and deletions for this file
    const fileStart = match.index;
    const nextFileMatch = fileRegex.exec(diff);
    const fileEnd = nextFileMatch ? nextFileMatch.index : diff.length;
    fileRegex.lastIndex = match.index + 1; // Reset to continue from current position
    
    const fileContent = diff.substring(fileStart, fileEnd);
    const additions = (fileContent.match(/^\+[^+]/gm) || []).length;
    const deletions = (fileContent.match(/^-[^-]/gm) || []).length;
    
    files.push({
      path: filePath,
      name: fileName,
      directory,
      additions,
      deletions,
      type: additions > 0 && deletions > 0 ? 'modified' : additions > 0 ? 'added' : 'deleted'
    });
  }
  
  return files;
}

// Build folder tree structure from files
function buildFileTree(files) {
  const tree = { name: '', children: {}, files: [] };
  
  files.forEach(file => {
    const parts = file.path.split('/');
    let current = tree;
    
    // Navigate/create folders
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      if (!current.children[part]) {
        current.children[part] = { name: part, children: {}, files: [] };
      }
      current = current.children[part];
    }
    
    // Add file to current folder
    current.files.push(file);
  });
  
  return tree;
}

// File Tree Component
function FileTree({ files, onFileClick, activeFile }) {
  // Collect all folder paths to expand by default
  const allFolderPaths = useMemo(() => {
    const paths = new Set(['']);
    files.forEach(file => {
      const parts = file.path.split('/');
      let currentPath = '';
      for (let i = 0; i < parts.length - 1; i++) {
        currentPath = currentPath ? `${currentPath}/${parts[i]}` : parts[i];
        paths.add(currentPath);
      }
    });
    return paths;
  }, [files]);
  
  const [expandedFolders, setExpandedFolders] = useState(allFolderPaths);
  const tree = useMemo(() => buildFileTree(files), [files]);
  
  // Update expanded folders when files change
  useEffect(() => {
    setExpandedFolders(allFolderPaths);
  }, [allFolderPaths]);
  
  const toggleFolder = (path) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };
  
  const renderTree = (node, path = '') => {
    const folders = Object.entries(node.children);
    
    return (
      <>
        {folders.map(([name, child]) => {
          const folderPath = path ? `${path}/${name}` : name;
          const isFolderExpanded = expandedFolders.has(folderPath);
          
          return (
            <div key={folderPath} className="tree-folder">
              <div 
                className="tree-folder-header"
                onClick={() => toggleFolder(folderPath)}
              >
                <IonIcon icon={isFolderExpanded ? chevronDown : chevronForward} className="tree-chevron" />
                <IonIcon icon={isFolderExpanded ? folderOpenOutline : folderOutline} className="tree-icon folder" />
                <span className="tree-name">{name}</span>
              </div>
              {isFolderExpanded && (
                <div className="tree-children">
                  {renderTree(child, folderPath)}
                </div>
              )}
            </div>
          );
        })}
        
        {node.files.map(file => (
          <div 
            key={file.path}
            className={`tree-file ${activeFile === file.path ? 'active' : ''}`}
            onClick={() => onFileClick(file.path)}
          >
            <IonIcon icon={documentOutline} className="tree-icon file" />
            <span className="tree-name">{file.name}</span>
            <div className="tree-stats">
              {file.additions > 0 && <span className="stat-add">+{file.additions}</span>}
              {file.deletions > 0 && <span className="stat-del">-{file.deletions}</span>}
            </div>
          </div>
        ))}
      </>
    );
  };
  
  return (
    <div className="file-tree">
      <div className="file-tree-header">
        <span>{files.length} files changed</span>
      </div>
      <div className="file-tree-content">
        {renderTree(tree)}
      </div>
    </div>
  );
}

export function CodeDiffViewer({ 
  diff, 
  fileContents = {},
  changedFiles = [],
  defaultView = 'side-by-side',
  showFileList = true,
  showFileTree = true,
  showViewToggle = true,
  title = null,
  headerActions = null,
  defaultCollapsed = true // Default to collapsed
}) {
  const [viewMode, setViewMode] = useState(defaultView);
  const [copied, setCopied] = useState(false);
  const [activeFile, setActiveFile] = useState(null);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [contentCollapsed, setContentCollapsed] = useState(defaultCollapsed);
  const [fullscreen, setFullscreen] = useState(false);
  const diffContentRef = useRef(null);

  useEffect(() => {
    if (fullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    const onKey = (e) => { if (e.key === 'Escape' && fullscreen) setFullscreen(false); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [fullscreen]);

  const handleExpandToggle = () => {
    if (fullscreen) {
      setFullscreen(false);
      setContentCollapsed(true);
      return;
    }
    setContentCollapsed(prev => !prev);
  };

  const fullDiff = useMemo(() => prettifyJsonDiff(combineDiffWithNewFiles(diff, fileContents)), [diff, fileContents]);

  // Parse files from diff
  const parsedFiles = useMemo(() => parseFilesFromDiff(fullDiff), [fullDiff]);
  const filesToShow = parsedFiles.length > 0 ? parsedFiles : 
    changedFiles.map(f => ({ path: f, name: f.split('/').pop(), additions: 0, deletions: 0 }));

  // Calculate total changes
  const totalChanges = useMemo(() => {
    return filesToShow.reduce((acc, file) => ({
      additions: acc.additions + (file.additions || 0),
      deletions: acc.deletions + (file.deletions || 0)
    }), { additions: 0, deletions: 0 });
  }, [filesToShow]);

  // Use Diff2HtmlUI which properly handles collapse/expand
  useEffect(() => {
    const container = diffContentRef.current;
    if (!container || !fullDiff || !fullDiff.trim()) return;

    try {
      const diff2htmlUi = new Diff2HtmlUI(container, fullDiff, {
        drawFileList: false,
        matching: 'lines',
        outputFormat: viewMode,
        renderNothingWhenEmpty: false,
        fileContentToggle: true, // Enable built-in collapse/expand
        synchronisedScroll: true,
        highlight: true, // Enable syntax highlighting
      });
      diff2htmlUi.draw();
      diff2htmlUi.highlightCode(); // Apply syntax highlighting
      
      // Ensure all files are expanded by default and inject stats
      container.querySelectorAll('.d2h-file-wrapper').forEach(wrapper => {
        wrapper.classList.remove('d2h-collapsed');
        const diffBody = wrapper.querySelector('.d2h-file-diff');
        if (diffBody) diffBody.style.display = '';
        
        // Inject file stats into header (like GitHub)
        const header = wrapper.querySelector('.d2h-file-header');
        const fileNameEl = wrapper.querySelector('.d2h-file-name');
        if (header && fileNameEl && !header.querySelector('.d2h-file-stats')) {
          const fileName = fileNameEl.getAttribute('title') || fileNameEl.textContent || '';
          // Find matching file stats from parsed files
          const fileInfo = parsedFiles.find(f => 
            fileName.includes(f.path) || fileName.includes(f.name) || 
            f.path.includes(fileName) || f.name === fileName.split('/').pop()
          );
          
          if (fileInfo && (fileInfo.additions > 0 || fileInfo.deletions > 0)) {
            const statsEl = document.createElement('span');
            statsEl.className = 'd2h-file-stats';
            statsEl.innerHTML = `
              ${fileInfo.additions > 0 ? `<span class="d2h-file-stats-add">+${fileInfo.additions}</span>` : ''}
              ${fileInfo.deletions > 0 ? `<span class="d2h-file-stats-del">-${fileInfo.deletions}</span>` : ''}
            `;
            header.appendChild(statsEl);
          }
        }
      });
      container.querySelectorAll('.d2h-file-collapse').forEach(toggle => {
        toggle.classList.remove('d2h-selected');
      });
      
      // Event delegation for collapse/expand toggle
      const handleClick = (e) => {
        // Check if we clicked on the collapse toggle or the file header
        const collapseToggle = e.target.closest('.d2h-file-collapse');
        const header = e.target.closest('.d2h-file-header');
        
        if (!collapseToggle && !header) return;
        
        // Don't toggle if clicking on the checkbox or its label
        if (e.target.matches('input, label')) return;
        
        const wrapper = (collapseToggle || header).closest('.d2h-file-wrapper');
        if (!wrapper) return;
        
        // Try multiple selectors for the diff body
        const diffBody = wrapper.querySelector('.d2h-file-diff') || 
                         wrapper.querySelector('.d2h-files-diff');
        const collapseIcon = wrapper.querySelector('.d2h-file-collapse');
        
        if (diffBody) {
          const isCurrentlyCollapsed = wrapper.classList.contains('d2h-collapsed') || 
                                       diffBody.style.display === 'none';
          
          if (isCurrentlyCollapsed) {
            // Expand
            diffBody.style.display = '';
            wrapper.classList.remove('d2h-collapsed');
            if (collapseIcon) collapseIcon.classList.remove('d2h-selected');
          } else {
            // Collapse
            diffBody.style.display = 'none';
            wrapper.classList.add('d2h-collapsed');
            if (collapseIcon) collapseIcon.classList.add('d2h-selected');
          }
        }
      };
      
      container.addEventListener('click', handleClick, true); // Use capture phase
      
      // Cleanup
      return () => {
        container.removeEventListener('click', handleClick, true);
      };
    } catch (err) {
      console.error('Error rendering diff:', err);
    }
  }, [fullDiff, viewMode, parsedFiles]);

  const handleCopyDiff = async () => {
    try {
      await navigator.clipboard.writeText(fullDiff);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleFileClick = (filePath) => {
    setActiveFile(filePath);
    // Scroll to file in diff view
    if (diffContentRef.current) {
      // Try multiple selectors to find the file
      const fileName = filePath.split('/').pop(); // Get just the filename
      let fileWrapper = null;
      
      // Try to find by various selectors
      const selectors = [
        `.d2h-file-name[title="${filePath}"]`,
        `.d2h-file-name[title*="${filePath}"]`,
        `.d2h-file-name[title*="${fileName}"]`,
      ];
      
      for (const selector of selectors) {
        const element = diffContentRef.current.querySelector(selector);
        if (element) {
          fileWrapper = element.closest('.d2h-file-wrapper');
          break;
        }
      }
      
      // Fallback: search through all file names for partial match
      if (!fileWrapper) {
        const allFileNames = diffContentRef.current.querySelectorAll('.d2h-file-name');
        for (const el of allFileNames) {
          const title = el.getAttribute('title') || el.textContent || '';
          if (title.includes(filePath) || title.includes(fileName)) {
            fileWrapper = el.closest('.d2h-file-wrapper');
            break;
          }
        }
      }
      
      if (fileWrapper) {
        // Expand the file if collapsed
        const diffBody = fileWrapper.querySelector('.d2h-file-diff, .d2h-files-diff');
        const collapseIcon = fileWrapper.querySelector('.d2h-file-collapse');
        if (diffBody && diffBody.style.display === 'none') {
          diffBody.style.display = '';
          fileWrapper.classList.remove('d2h-collapsed');
          if (collapseIcon) collapseIcon.classList.remove('d2h-selected');
        }
        
        // Scroll to the file
        fileWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  if (!diff || !diff.trim()) {
    return (
      <div className="code-diff-viewer empty">
        <p>No changes to display</p>
      </div>
    );
  }

  return (
    <div className={`code-diff-viewer ${showFileTree && sidebarVisible ? 'with-tree' : ''} ${fullscreen ? 'diff-fullscreen' : ''}`}>
      {/* Header */}
      <div className="diff-viewer-header">
        <div className="diff-viewer-title-section">
          <button 
            className="content-collapse-toggle"
            onClick={handleExpandToggle}
            title={contentCollapsed ? 'Expand' : 'Collapse'}
          >
            <IonIcon icon={contentCollapsed && !fullscreen ? chevronForward : chevronDown} />
          </button>
          {showFileTree && (
            <button 
              className={`diff-sidebar-toggle ${sidebarVisible ? 'active' : ''}`}
              onClick={() => setSidebarVisible(!sidebarVisible)}
              title={sidebarVisible ? 'Hide file tree' : 'Show file tree'}
            >
              <IonIcon icon={terminalOutline} />
            </button>
          )}
          {title && (
            <h3 
              className="diff-viewer-title clickable" 
              onClick={handleExpandToggle}
              title={contentCollapsed ? 'Expand' : 'Collapse'}
            >
              {title}
            </h3>
          )}
          <button
            className="diff-fullscreen-toggle"
            onClick={() => setFullscreen(prev => !prev)}
            title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            <IonIcon icon={fullscreen ? contractOutline : expandOutline} />
          </button>
        </div>
        
        <div className="diff-viewer-actions">
          {(totalChanges.additions > 0 || totalChanges.deletions > 0) && (
            <div className="diff-total-changes">
              {totalChanges.additions > 0 && (
                <span className="total-add">+{totalChanges.additions}</span>
              )}
              {totalChanges.deletions > 0 && (
                <span className="total-del">-{totalChanges.deletions}</span>
              )}
            </div>
          )}
          
          {showViewToggle && (
            <div className="view-toggle">
              <button
                className={`toggle-btn ${viewMode === 'side-by-side' ? 'active' : ''}`}
                onClick={() => setViewMode('side-by-side')}
                title="Side by side view"
              >
                <IonIcon icon={swapHorizontalOutline} />
              </button>
              <button
                className={`toggle-btn ${viewMode === 'line-by-line' ? 'active' : ''}`}
                onClick={() => setViewMode('line-by-line')}
                title="Unified view"
              >
                <IonIcon icon={listOutline} />
              </button>
            </div>
          )}
          
          {headerActions && headerActions}
          
          <button className="copy-btn" onClick={handleCopyDiff} title="Copy diff">
            <IonIcon icon={copied ? checkmarkOutline : copyOutline} />
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div 
        className={`diff-viewer-body ${!sidebarVisible ? 'sidebar-hidden' : ''} ${contentCollapsed ? 'collapsed' : ''}`}
      >
        {/* File Tree Sidebar */}
        {showFileTree && sidebarVisible && filesToShow.length > 0 && (
          <FileTree 
            files={filesToShow}
            onFileClick={handleFileClick}
            activeFile={activeFile}
          />
        )}

        {/* Diff Content */}
        <div 
          ref={diffContentRef}
          className={`diff-content ${viewMode}`}
        />
      </div>
    </div>
  );
}

export default CodeDiffViewer;
